import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import {
  getGraphicsDashboardv2,
  getGraphicsDashboard2,
  deleteElement2,
  updateOrderAPI,
} from "../../apis/apiDashboard";
import Greetings from "./Greetings/Greetings";
import { Breadcrum } from "../MainComponents/Breadcrumb/Breadcrumb";
import { Loading } from "../Loading/String/Loading";
import IndicatorsBoard from "./Graphic/charts/EthicsChart/IndicatorsBoard";
import DynamicChart from "./Graphic/charts/EthicsChart/DynamicChart";
import MexicoMap from "./Graphic/charts/EthicsChart/MexicoMap";
import TableComponent from "./Graphic/charts/EthicsChart/TableComponent";
import DateSelectorWithButton from "./Graphic/charts/EthicsChart/utils/DateSelectorWithButton";
import ModalGraph from "../Modal/ModalGraph";
import { FormCheck } from "react-bootstrap";
import { Grid2 } from "@mui/material";
import Icon from "../Icon/Icon";
import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import DropdownButton from "react-bootstrap/DropdownButton";
import Dropdown from "react-bootstrap/Dropdown";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import { EGIconButton } from "../MainComponents/Button/IconButton/IconButton";

const toLocalISO = (d) => {
  const x = new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const day = String(x.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const isValidRange = (start, end) => {
  if (!(start instanceof Date) || !(end instanceof Date)) return false;
  const toDay = (d) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const s = toDay(start);
  const e = toDay(end);
  const today = toDay(new Date());
  if (e > today || e < s) return false;
  const oneYearLater = toDay(
    new Date(start.getFullYear() + 1, start.getMonth(), start.getDate()),
  );
  return e <= oneYearLater;
};

export const Dashboardv2 = () => {
  const [t] = useTranslation("dashboard");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [graphics, setGraphics] = useState({});
  const [indicators, setIndicators] = useState({});
  const [order, setOrder] = useState({});
  const [chartTypes, setChartTypes] = useState([]);
  const [chartDates, setChartDates] = useState(30);
  // Antes: useState([]) — array vacío es truthy en JS, causaba que validaciones como if(!start) no funcionaran
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showTable, setShowTable] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [ingroups, setIngroups] = useState(null);
  const [selectedIngroup, setSelectedIngroup] = useState("general");

  const fetchData = useCallback(async () => {
    const response = await getGraphicsDashboard2();
    if (!response?.graphics) return;

    setGraphics(response.graphics.graphicsv2?.graphics || []);
    setIndicators(response.graphics.graphicsv2?.indicators || []);
    setOrder(response.order || {});

    // Guardar ingroups si existen
    if (response.graphics.graphicsv2?.ingroups) {
      setIngroups(response.graphics.graphicsv2.ingroups);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // useEffect para aplicar fechas iniciales SOLO cuando ya cargaron los datos
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (initialized) return; // 👈 evita que vuelva a pisar las fechas
    const hasGraphicsData = graphics && Object.keys(graphics).length > 0;
    const hasIndicatorsData = indicators && Object.keys(indicators).length > 0;
    if (!hasGraphicsData && !hasIndicatorsData) return;

    // Calcular fechas iniciales (últimos 30 días)
    const today = new Date();
    const end = toLocalISO(today);
    const start = new Date(today);
    start.setDate(start.getDate() - 30);
    const startISO = toLocalISO(start);

    setChartDates(30);
    setStartDate(start);
    setEndDate(today);

    const updateRange = (data) =>
      Object.fromEntries(
        Object.entries(data).map(([id, item]) => {
          const globalArray = item.global || [];
          let updatedGlobal = globalArray.map((g) => {
            if (g.key === "created_at__date__gte")
              return { ...g, value: startISO };
            if (g.key === "created_at__date__lte") return { ...g, value: end };
            return g;
          });
          // Si las keys de fecha no existen en global[], agregarlas
          if (!updatedGlobal.some((g) => g.key === "created_at__date__gte")) {
            updatedGlobal.push({
              key: "created_at__date__gte",
              value: startISO,
              source: "field",
            });
          }
          if (!updatedGlobal.some((g) => g.key === "created_at__date__lte")) {
            updatedGlobal.push({
              key: "created_at__date__lte",
              value: end,
              source: "field",
            });
          }
          return [id, { ...item, global: updatedGlobal }];
        }),
      );

    setGraphics((prev) => updateRange(prev));
    setIndicators((prev) => updateRange(prev));
    setInitialized(true);
  }, [graphics, indicators, initialized]);
  // Nota: las dependencias [graphics, indicators] se modifican dentro de este mismo efecto.
  // Se recomienda dejar solo [initialized] para evitar re-ejecuciones innecesarias.

  const [visibleCount, setVisibleCount] = useState(10); // 👈 10 gráficas iniciales

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((prev) => prev + 10); // 👈 carga 10 más
        }
      },
      { threshold: 1 },
    );

    const sentinel = document.querySelector("#sentinel");
    if (sentinel) observer.observe(sentinel);

    return () => observer.disconnect();
  }, []);

  const toggleShowTable = (index) => {
    setShowTable((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleChangeType = (index, newType) => {
    const updatedTypes = [...chartTypes];
    updatedTypes[index] = newType;
    setChartTypes(updatedTypes);
  };

  const deleteGraphic = async (id) => {
    await deleteElement2({ graph_id: id });
    setGraphics((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
    setIndicators((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  const handleRangeDates = (start, end) => {
    if (!start || !end) return;
    const startISO = toLocalISO(start);
    const endISO = toLocalISO(end);

    const updateRange = (data) =>
      Object.fromEntries(
        Object.entries(data).map(([id, item]) => {
          const globalArray = item.global || [];
          let updatedGlobal = globalArray.map((g) => {
            if (g.key === "created_at__date__gte")
              return { ...g, value: startISO };
            if (g.key === "created_at__date__lte")
              return { ...g, value: endISO };
            return g;
          });
          // Si las keys de fecha no existen en global[] agregarlas
          if (!updatedGlobal.some((g) => g.key === "created_at__date__gte")) {
            updatedGlobal.push({
              key: "created_at__date__gte",
              value: startISO,
              source: "field",
            });
          }
          if (!updatedGlobal.some((g) => g.key === "created_at__date__lte")) {
            updatedGlobal.push({
              key: "created_at__date__lte",
              value: endISO,
              source: "field",
            });
          }
          return [id, { ...item, global: updatedGlobal }];
        }),
      );

    setGraphics((prev) => updateRange(prev));
    setIndicators((prev) => updateRange(prev));
  };

  const handleChangeDate = (days) => {
    setChartDates(days);

    // si es numérico -> calculamos fechas
    const numDays = Number(days);
    if (!isNaN(numDays) && days !== "range") {
      const today = new Date();
      const endISO = toLocalISO(today);
      const start = new Date();
      start.setDate(start.getDate() - numDays);
      const startISO = toLocalISO(start);

      setStartDate(start);
      setEndDate(today);

      const updateRange = (data) =>
        Object.fromEntries(
          Object.entries(data).map(([id, item]) => {
            const globalArray = item.global || [];
            let updatedGlobal = globalArray.map((g) => {
              if (g.key === "created_at__date__gte")
                return { ...g, value: startISO };
              if (g.key === "created_at__date__lte")
                return { ...g, value: endISO };
              return g;
            });
            // Si las keys de fecha no existen en global[] agregarlas
            if (!updatedGlobal.some((g) => g.key === "created_at__date__gte")) {
              updatedGlobal.push({
                key: "created_at__date__gte",
                value: startISO,
                source: "field",
              });
            }
            if (!updatedGlobal.some((g) => g.key === "created_at__date__lte")) {
              updatedGlobal.push({
                key: "created_at__date__lte",
                value: endISO,
                source: "field",
              });
            }
            return [id, { ...item, global: updatedGlobal }];
          }),
        );

      setGraphics((prev) => updateRange(prev));
      setIndicators((prev) => updateRange(prev));
    }

    // si es "range", asegurar que startDate/endDate tengan valores para los DatePickers
    if (days === "range" && (!startDate || !endDate)) {
      const today = new Date();
      const defaultStart = new Date(today);
      defaultStart.setDate(defaultStart.getDate() - 30);
      if (!startDate) setStartDate(defaultStart);
      if (!endDate) setEndDate(today);
    }
  };

  // 👇 Mover gráfica arriba o abajo
  const moveGraphic = async (index, direction) => {
    const newGraphics = [...orderedGraphics];

    if (direction === "up" && index > 0) {
      [newGraphics[index - 1], newGraphics[index]] = [
        newGraphics[index],
        newGraphics[index - 1],
      ];
    } else if (direction === "down" && index < newGraphics.length - 1) {
      [newGraphics[index + 1], newGraphics[index]] = [
        newGraphics[index],
        newGraphics[index + 1],
      ];
    } else {
      return;
    }

    // Reasignar orden localmente
    const updatedGraphics = newGraphics.map((item, idx) => ({
      ...item,
      orden: idx + 1,
    }));
    setGraphics(Object.fromEntries(updatedGraphics.map((g) => [g.id, g])));

    // Persistir en backend
    try {
      await updateOrderAPI({
        graph_id: newGraphics[index].id,
        direction: direction,
      });
    } catch (error) {
      console.error("Error al actualizar orden en backend:", error);
    }
  };

  // 👇 Mover indicador arriba o abajo (mismo patrón que moveGraphic)
  const moveIndicator = async (id, direction) => {
    const newIndicators = [...orderedIndicators];
    const index = newIndicators.findIndex((item) => item.id === id);
    if (index === -1) return;

    if (direction === "up" && index > 0) {
      [newIndicators[index - 1], newIndicators[index]] = [
        newIndicators[index],
        newIndicators[index - 1],
      ];
    } else if (direction === "down" && index < newIndicators.length - 1) {
      [newIndicators[index + 1], newIndicators[index]] = [
        newIndicators[index],
        newIndicators[index + 1],
      ];
    } else {
      return;
    }

    // Reasignar orden localmente
    const updatedIndicators = newIndicators.map((item, idx) => ({
      ...item,
      orden: idx + 1,
    }));
    setIndicators(Object.fromEntries(updatedIndicators.map((g) => [g.id, g])));

    // Persistir en backend
    try {
      await updateOrderAPI({
        graph_id: id,
        direction: direction,
      });
    } catch (error) {
      console.error("Error al actualizar orden en backend:", error);
    }
  };

  // Ordenar indicadores por `orden` (con fallback) igual que las gráficas
  const orderedIndicators = Object.entries(indicators)
    .map(([id, item], index) => ({
      id,
      ...item,
      orden: item.orden ?? index + 1, // <-- asigna orden si no existe
    }))
    .sort((a, b) => (a.orden || 9999) - (b.orden || 9999));

  /** Loader para indicadores */
  const indicatorsQueries = useQueries({
    queries: orderedIndicators.map((dataItem) => ({
      queryKey: ["indicator", dataItem.id, dataItem],
      queryFn: () => getGraphicsDashboardv2({ filters: dataItem }),
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 30, // 30 minutos en cache antes de borrarse
      refetchOnWindowFocus: false, // no refetch al volver a la pestaña
      refetchOnMount: false, // no refetch al volver a montar el componente
      refetchOnReconnect: false,
    })),
  });

  // Verificar si alguna query está cargando
  const isIndicatorsLoading = indicatorsQueries.some((q) => q.isLoading);

  // Armar array de indicadores ya cargados
  const loadedIndicators = indicatorsQueries
    .map((q, i) => {
      if (q.data?.filters_summary) {
        return {
          id: orderedIndicators[i].id,
          title: orderedIndicators[i].title?.[0]?.title || "Sin título",
          orden: orderedIndicators[i].orden,
          data: q.data.filters_summary,
        };
      }
      return null;
    })
    .filter(Boolean);

  // Render de indicadores
  {
    Object.keys(indicators).length > 0 && (
      <div style={{ marginBottom: "2rem" }}>
        {isIndicatorsLoading ? (
          <Loading /> 
        ) : (
          <IndicatorsBoard
            indicators={loadedIndicators}
            onDelete={deleteGraphic}
          />
        )}
      </div>
    );
  }

  /*Orden de las graficas*/

  // Filtrar por ingroup seleccionado si existen ingroups
  const filteredGraphics = ingroups
    ? Object.fromEntries(
        Object.entries(graphics).filter(
          ([, item]) => item.ingroup === selectedIngroup,
        ),
      )
    : graphics;

  const orderedGraphics = Object.entries(filteredGraphics)
    .map(([id, item], index) => ({
      id,
      ...item,
      orden: item.orden ?? index + 1, // <-- asigna orden si no existe
    }))
    .sort((a, b) => (a.orden || 9999) - (b.orden || 9999));

  // Una gráfica es histórica si su tipo persistido es "historic".
  // Para estas pedimos al backend el desglose mensual (include_monthly),
  // que devuelve 24 meses (año anterior + actual) ya con ceros rellenos.
  const isHistoricGraphic = (item) => item?.title?.[0]?.type === "historic";

  /** Loader para gráficas */
  const graphicsQueries = useQueries({
    queries: orderedGraphics.map((dataItem) => ({
      queryKey: ["graphic", dataItem.id, dataItem],
      queryFn: () =>
        getGraphicsDashboardv2({
          filters: isHistoricGraphic(dataItem)
            ? { ...dataItem, include_monthly: true }
            : dataItem,
        }),
      staleTime: 1000 * 60 * 5,
    })),
  });

  return (
    <div className="dashboard">
      <Greetings />
      <Breadcrum
        items={[
          { label: t("dashboard.home"), route: "/" },
          { label: "Dashboard" },
        ]}
      />
      <DateSelectorWithButton
        chartDates={chartDates}
        setChartDates={setChartDates}
        startDate={startDate}
        endDate={endDate}
        onRangeChange={(type, value) => {
          if (type === "start") {
            setStartDate(value);
            if (endDate && isValidRange(value, endDate))
              handleRangeDates(value, endDate);
          } else {
            setEndDate(value);
            if (startDate && isValidRange(startDate, value))
              handleRangeDates(startDate, value);
          }
        }}
        onchartDates={handleChangeDate}
        fetchData={fetchData}
        ingroups={ingroups}
        selectedIngroup={selectedIngroup}
        onIngroupChange={(value) => {
          setSelectedIngroup(value);
          setVisibleCount(10);
        }}
      />

      {/* INDICADORES */}
      {Object.keys(indicators).length > 0 && (
        <div style={{ marginBottom: "2rem" }}>
          {loadedIndicators.length === 0 ? (
            <Loading />
          ) : (
            <IndicatorsBoard
              indicators={loadedIndicators}
              onDelete={deleteGraphic}
              onMove={moveIndicator}
            />
          )}
        </div>
      )}

      {/* GRÁFICAS */}
      {graphicsQueries.slice(0, visibleCount).map((q, index) => {
        const dataItem = orderedGraphics[index];
        const id = dataItem.id;
        if (q.isLoading) return <Loading key={id} />;

        // Para gráficas históricas preferimos monthly_breakdown (24 meses con
        // ceros rellenos). Si el backend no lo envía (versión vieja) o no es
        // histórica, caemos a filters_summary. La forma del item es idéntica
        // ({ label, filters, count }), así que el resto del pipeline no cambia.
        const chartItems =
          isHistoricGraphic(dataItem) && q.data?.monthly_breakdown
            ? q.data.monthly_breakdown
            : q.data?.filters_summary;

        if (!chartItems) return null;

        const group = {
          id,
          title: dataItem.title?.[0]?.title || "Sin título",
          type: dataItem.title?.[0]?.type || "desconocido",
          data: chartItems,
        };

        return (
          <Box
            key={group.id}
            sx={{
              bgcolor: "background.paper",
              mb: 4,
              p: 2,
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            {/* 🔹 Header con título + opciones */}
            <Grid2
              container
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Grid2>
                <h2>{group.title}</h2>
              </Grid2>

              <Grid2
                sx={{ display: "flex", alignItems: "center", gap: 2 }}
              >
                {/* Flechas para mover arriba/abajo */}
                <Box sx={{ display: "flex", gap: 1 }}>
                  <EGIconButton
                    iconSize={16}
                    className="graphic-option"
                    iconName="arrow_up_4"
                    onClick={() => moveGraphic(index, "up")}
                    title={t("Graphics.move_up")}
                  />
                  <EGIconButton
                    iconSize={16}
                    className="graphic-option"
                    iconName="arrow_down"
                    onClick={() => moveGraphic(index, "down")}
                    title={t("Graphics.move_down")}
                  />
                </Box>

                {/* Cambiar tipo de gráfica */}
                <FormControl sx={{ minWidth: 120 }} size="small">
                  <InputLabel id={`select-type-${index}`}>Tipo</InputLabel>
                  <Select
                    labelId={`select-type-${index}`}
                    value={chartTypes[index] || "pie"}
                    label="Tipo"
                    onChange={(e) => handleChangeType(index, e.target.value)}
                  >
                    <MenuItem value="pie">Pie</MenuItem>
                    <MenuItem value="donut">Donut</MenuItem>
                    <MenuItem value="bar">Bar</MenuItem>
                    <MenuItem value="line">Line</MenuItem>
                    <MenuItem value="nightingale">Nightingale</MenuItem>
                    <MenuItem value="rose">Rose</MenuItem>
                  </Select>
                </FormControl>

                {/* Toggle tabla ↔ gráfica */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Icon
                    size={16}
                    className="graphic-option"
                    name="bar_chart_bold"
                  />
                  <FormCheck
                    type="switch"
                    checked={!!showTable[index]}
                    onChange={() => toggleShowTable(index)}
                  />
                  <Icon
                    size={16}
                    className="data-option"
                    name="outline_chart_bold"
                  />

                  {/* Opciones extra (borrar, etc.) */}
                  <DropdownButton
                    id={`dropdown-button-${index}`}
                    drop="end"
                    variant="light"
                    title={<Icon size={18} name="more_vert" />}
                  >
                    <Dropdown.Item onClick={() => deleteGraphic(group.id)}>
                      {t("Graphics.delete")}
                    </Dropdown.Item>
                  </DropdownButton>
                </Box>
              </Grid2>
            </Grid2>

            {/* 🔹 Contenido de la gráfica */}
            <Grid2 size={{ xs: 12 }} sx={{ borderTop: "2px solid grey", pt: 2 }}>
              {showTable[index] ? (
                <TableComponent
                  data={group.data}
                  fechas={{ inicio: startDate, fin: endDate }}
                />
              ) : group.type === "maps_mex" ? (
                <MexicoMap chartData={group} />
              ) : (
                <DynamicChart
                  chartData={group}
                  graphicTitle={group.title}
                  opacity={0.8}
                  type={chartTypes[index] || "pie"}
                />
              )}
            </Grid2>
          </Box>
        );
      })}

      {/* 👇 El sentinel que dispara el scroll infinito */}
      <div id="sentinel" style={{ height: "50px" }} />

      {/* Dashboard v1 legacy eliminado en mercadotest: sólo se muestra v2 para que
          todo responda al select de fechas/opciones. Las gráficas viejas se migran a v2. */}

      {modalOpen && (
        <ModalGraph
          show={modalOpen}
          onHide={() => setModalOpen(false)}
          stateLoadingIndicators={() => {}}
          stateLoadingGraphics={() => {}}
        />
      )}
    </div>
  );
};
